import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import { motion } from 'framer-motion';
import { Heart, Trash2, Edit, ChevronDown, ChevronUp, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BloodPressureHistory = ({ patientId, onEdit }) => {
  const [bpRecords, setBpRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    if (patientId) {
      fetchBPRecords();
    }
  }, [patientId]);

  const fetchBPRecords = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/blood-pressure/${patientId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBpRecords(response.data.sort((a, b) => new Date(b.date + ' ' + b.time) - new Date(a.date + ' ' + a.time)));
      setLoading(false);
    } catch (error) {
      console.error('Error fetching BP records:', error);
      setLoading(false);
    }
  };

  const handleDelete = async (bpId) => {
    const token = localStorage.getItem('token');
    try {
      await axios.delete(`${API}/blood-pressure/${bpId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Record deleted');
      fetchBPRecords();
    } catch (error) {
      toast.error('Failed to delete record');
    }
  };

  const formatDate = (dateStr) => {
    try {
      return format(new Date(dateStr), 'MMM dd, yyyy');
    } catch {
      return dateStr;
    }
  };

  const getBPStatus = (systolic, diastolic) => {
    if (systolic >= 140 || diastolic >= 90) {
      return { label: 'High', color: '#F9A7A7' };
    } else if (systolic >= 130 || diastolic >= 80) {
      return { label: 'Elevated', color: '#F9D6A7' };
    } else {
      return { label: 'Normal', color: '#A2DAD3' };
    }
  };

  if (loading) {
    return <div className="text-center py-4">Loading...</div>;
  }

  return (
    <div className="card" data-testid="bp-history">
      <div className="flex items-center gap-2 mb-4">
        <Heart size={20} style={{ color: '#F9A7A7' }} />
        <h2 className="text-xl font-semibold" style={{ color: '#F9A7A7' }}>Blood Pressure History</h2>
      </div>

      {bpRecords.length === 0 ? (
        <p className="text-gray-500 text-center py-8">No blood pressure records yet</p>
      ) : (
        <>
        <div className="space-y-3">
          {(showAll ? bpRecords : bpRecords.slice(0, 3)).map((record, index) => {
            const status = getBPStatus(record.systolic, record.diastolic);
            return (
              <motion.div
                key={record.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="p-4 rounded-xl relative"
                style={{ backgroundColor: '#fff5f5', border: '2px solid #F9A7A7' }}
                data-testid={`bp-record-${record.id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <p className="font-medium">{formatDate(record.date)}</p>
                      <span className="text-sm text-gray-600">{record.time}</span>
                      <span
                        className="text-xs px-2 py-1 rounded-full"
                        style={{ backgroundColor: status.color, color: 'white' }}
                      >
                        {status.label}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 mb-2">
                      <div>
                        <span className="text-2xl font-bold" style={{ color: '#F9A7A7' }}>
                          {record.systolic}/{record.diastolic}
                        </span>
                        <span className="text-sm text-gray-600 ml-2">mmHg</span>
                      </div>
                      {record.pulse && (
                        <div>
                          <span className="text-lg font-semibold">{record.pulse}</span>
                          <span className="text-xs text-gray-600 ml-1">bpm</span>
                        </div>
                      )}
                    </div>
                    {record.notes && (
                      <p className="text-sm text-gray-600 italic mt-2">{record.notes}</p>
                    )}
                    {record.image_url && record.image_url.trim() !== '' && (
                      <div className="mt-2 flex items-center gap-2">
                        <div 
                          className="relative cursor-pointer group"
                          onClick={() => window.open(`${BACKEND_URL}${record.image_url}`, '_blank')}
                        >
                          <img 
                            src={`${BACKEND_URL}${record.image_url}`} 
                            alt="BP reading" 
                            className="h-16 w-16 object-cover rounded border-2 border-gray-200 group-hover:border-teal-400 transition-all"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                          <div 
                            className="hidden h-16 w-16 items-center justify-center rounded border-2 border-gray-200 bg-gray-100"
                          >
                            <ImageIcon size={24} className="text-gray-400" />
                          </div>
                          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 rounded transition-all flex items-center justify-center">
                            <span className="text-xs text-white opacity-0 group-hover:opacity-100 font-medium">View</span>
                          </div>
                        </div>
                        <span className="text-xs text-gray-500">Click to view full size</span>
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2">
                    {onEdit && (
                      <button
                        onClick={() => onEdit(record)}
                        className="text-blue-500 hover:text-blue-700 p-1"
                        title="Edit"
                      >
                        <Edit size={14} />
                      </button>
                    )}
                    <button
                      onClick={() => handleDelete(record.id)}
                      className="text-red-500 hover:text-red-700 p-1"
                      data-testid={`delete-bp-${record.id}`}
                      title="Delete"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
        
        {bpRecords.length > 3 && (
          <button
            onClick={() => setShowAll(!showAll)}
            className="w-full text-sm py-2 rounded-lg flex items-center justify-center gap-2 mt-3"
            style={{ color: '#F9A7A7', backgroundColor: '#fff5f5' }}
          >
            {showAll ? (
              <>
                Show Less <ChevronUp size={16} />
              </>
            ) : (
              <>
                Show More ({bpRecords.length - 3} more) <ChevronDown size={16} />
              </>
            )}
          </button>
        )}
        </>
      )}
    </div>
  );
};

export default BloodPressureHistory;
